import type { Workitem } from '@rhythmic/db/types'
import type {
  BacklogContext,
  CreateWorkitemPayload,
  KanbanWorkitemMutationPayload,
  WorkitemSearchFilters,
} from '@rhythmic/types'
import type { Transaction } from '@tanstack/vue-db'
import { defineStore, storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useRhythmicLogger } from '#imports'
import useTeamContext from '../team/useTeamContext'
import createMutationHandlers from './useBacklogContext.mutators'
import useBacklogContextQueries from './useBacklogContext.queries'

export type { BacklogContext } from '@rhythmic/types'

export const useBacklogContext = defineStore('backlogStore', () => {
  const logger = useRhythmicLogger('backlogContextStore')
  const teamContext = useTeamContext()
  const { workspaceId, workspaceSlug, team, teamSlug } = storeToRefs(teamContext)

  const queries = useBacklogContextQueries()
  const resolvedTeamId = computed<string | undefined>(() => team.value?.id ?? undefined)
  queries.init({ logger, teamId: resolvedTeamId, workspaceId })

  const context = computed<BacklogContext | undefined>(() => {
    if (!team.value || !team.value.id || !team.value.workspace_id) return undefined

    return {
      teamId: team.value.id,
      workspaceId: team.value.workspace_id,
    }
  })
  const mutators = createMutationHandlers(context)

  const workitems = computed<Workitem[]>(() => queries.workitems.value ?? [])

  function setSearchFilters(filters: WorkitemSearchFilters | undefined) {
    if (!filters) {
      queries.setSearchFilters(undefined, {
        teamId: resolvedTeamId.value,
        workspaceId: workspaceId.value,
      })
      return
    }

    queries.setSearchFilters(
      {
        statuses: filters.statuses?.slice() ?? undefined,
        types: filters.types?.slice() ?? undefined,
      },
      {
        teamId: resolvedTeamId.value,
        workspaceId: workspaceId.value,
      },
    )
  }

  function createWorkitem(payload: CreateWorkitemPayload): Transaction<Workitem> {
    return mutators.createWorkitem(payload)
  }

  function updateWorkitem(payload: KanbanWorkitemMutationPayload): Transaction {
    return mutators.updateWorkitem(payload)
  }

  function deleteWorkitem(id: string): Transaction<Workitem> {
    return mutators.deleteWorkitem(id)
  }

  return {
    queries,
    workspaceSlug,
    teamSlug,
    workspaceId,
    team,
    workitems,
    setSearchFilters,
    createWorkitem,
    updateWorkitem,
    deleteWorkitem,
  }
})

export default useBacklogContext
